import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router";

/** 確認の段に付ける印（履歴の state）。 */
const confirmState = { contractStep: "confirm" } as const;

const isConfirmState = (state: unknown): boolean =>
    typeof state === "object" && state !== null
    && "contractStep" in state && state.contractStep === confirmState.contractStep;

/** 確認を履歴の 1 段として扱うもの。 */
export interface ConfirmEntry {
    /** 今の履歴が確認の段で、<b>見せる確認の結果を持っている</b>か。 */
    readonly showsConfirm: boolean;
    /** 確認へ移る（履歴を 1 つ積む）。 */
    readonly push: () => void;
    /** 入力へ戻る（履歴を 1 つ戻る。ブラウザの「戻る」と同じ）。 */
    readonly back: () => void;
}

/**
 * 申し込み・プラン変更の確認を<b>同じ URL のまま履歴に 1 段積む</b>（docs/05 §11.9）。
 * ブラウザの「戻る」で入力へ戻り、入力は画面が持ったまま残る。「進む」で確認へ戻れる。
 *
 * <b>確認の結果（金額・無料期間）は画面が持つだけ</b>で、履歴にもサーバにも置かない（Implem.Portal.Backend の docs/05）。
 * そのため、確認の段を読み込み直したとき・画面を離れてから「進む」で来たときは結果が無く、
 * <b>入力へ戻す</b>（印を外して入力の段に置き換える）。印を残すと、次に確認へ進んでから
 * 「戻る」を押したとき、入力のはずの段で確認が出る。
 *
 * <c>hasConfirmation</c> は確認の結果を持っているか。入力を変えたら結果を捨てる（使い回すと、
 * 見せた金額と送る入力が食い違う）ので、「戻る」で入力を直してから「進む」を押しても入力のまま。
 */
export const useConfirmEntry = (hasConfirmation: boolean): ConfirmEntry => {
    const location = useLocation();
    const navigate = useNavigate();
    const { pathname, search, hash } = location;
    const here = { pathname, search, hash };
    const onConfirmEntry = isConfirmState(location.state);
    const stale = onConfirmEntry && !hasConfirmation;

    useEffect(() => {
        if (stale) {
            void navigate({ pathname, search, hash }, { replace: true, state: null });
        }
    }, [stale, navigate, pathname, search, hash]);

    return {
        showsConfirm: onConfirmEntry && hasConfirmation,
        push: () => {
            void navigate(here, { state: confirmState });
        },
        back: () => {
            void navigate(-1);
        },
    };
};
